import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {List, Set} from 'immutable';
import {isEmail} from 'validator';
import track from 'react-tracking';
import errorCodes from 'client/errorCodes';

import withTracking from 'lib/hocs/withTracking';
import {doesUserBelongToAnyLicensedClassrooms} from 'lib/UserAccessUtil';
import ArrayToReadableFragment from 'lib/hocs/arrayToReadableFragment';
import {arrayToReadableString} from 'lib/stringUtils';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';

import {addToast, Banner, Dialogue, LoadingSpinnerLong, TextAreaField} from '@albert-io/atomic';
import TrackedButton from 'client/components/Tracking/TrackedButton.react';
import HardCapWarningBanner from 'client/components/HardCapWarningBanner/HardCapWarningBanner.react';

import {isManagedAccount} from 'client/Classrooms/shared/enrollUtils';

import {getModelForResourceType} from 'resources/modelRegistry';

import {ClassroomInvitationModelV1} from 'resources/GeneratedModels/ClassroomInvitation/ClassroomInvitationModel.v1';

import IBESummary from './IBEDialogues/IBESummary.react';
import IBEConfirmEmails from './IBEDialogues/IBEConfirmEmails.react';
import IBEUnlicensedError from './IBEDialogues/IBEUnlicensedError.react';
import UploadCSVBtn from './UploadCSVBtn/UploadCSVBtn.react';
import EmailChips from './EmailChips/EmailChips.react';

import './invite-by-email.scss';

const delimiter = new RegExp(/[\n\r;, ]/, 'gi');

const TrackedTextAreaField = withTracking(TextAreaField, {
  onPaste: {content: (e) => e.clipboardData.getData('Text'), event: 'paste', type: null}
});

@track({component: 'InviteByEmail'})
class InviteByEmail extends React.Component {
  static propTypes = {
    classroomId: PropTypes.string,
    emailDomains: ImmutablePropTypes.listOf(getModelForResourceType('email_domain_v1')),
    handleClose: PropTypes.func.isRequired,
    inModal: PropTypes.bool,
    invitations: ImmutablePropTypes.listOf(getModelForResourceType('classroom_invitation_v1')),
    onSend: PropTypes.func,
    restrictClassroomEnrollment: PropTypes.bool,
    students: ImmutablePropTypes.listOf(getModelForResourceType('student_v2')),
    tracking: PropTypes.object,
    hardCap: PropTypes.bool,
    seatsRemaining: PropTypes.number
  };

  static defaultProps = {
    emailDomains: List(),
    invitations: List(),
    onSend: () => {},
    students: List()
  };

  constructor() {
    super();

    this.state = {
      emails: Set(),
      error: null,
      fulfilledInvitations: [],
      mustConfirmSubmission: false,
      rejectedInvitations: [],
      hardCapRejectedInvitations: [],
      requestInFlight: false,
      value: ''
    };
  }

  handleChange = (e) =>
    this.setState({
      error: null,
      value: e.target.value
    });

  handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.props.tracking.trackEvent({content: null, event: 'keyboard', type: 'enter'});
      this.handleSubmit();
    }
  };

  handleSubmit = () => {
    const input = this.state.value.trim().split(delimiter);
    const {restrictClassroomEnrollment} = this.props;
    const parsedInput = input
      .filter((item) => item) // filter out empty strings
      .reduce(
        (acc, curr) => {
          if (!isEmail(curr)) {
            acc.invalidItems.push({
              email: curr,
              reason: 'One or more of your entries is not a valid email.'
            });
            return acc;
          }
          if (
            restrictClassroomEnrollment &&
            !this.props.emailDomains.isEmpty() &&
            !isManagedAccount(curr, this.props.emailDomains)
          ) {
            const domainNames = this.props.emailDomains.map((domain) => domain.getName());
            acc.invalidItems.push({
              email: curr,
              reason: `Invitations are restricted to emails ending in ${arrayToReadableString(
                [...domainNames],
                'or'
              )}.`
            });
            return acc;
          }
          acc.validItems.push(curr);
          return acc;
        },
        {
          invalidItems: [],
          validItems: []
        }
      );

    if (parsedInput.invalidItems.length) {
      this.props.tracking.trackEvent({
        content: parsedInput.invalidItems[0].reason,
        event: 'error',
        type: null
      });
    }

    this.setState(({emails}) => ({
      emails: emails.union(parsedInput.validItems),
      error: parsedInput.invalidItems.length ? parsedInput.invalidItems[0].reason : null,
      value: parsedInput.invalidItems.map((item) => item.email).join(';\n')
    }));
  };

  @track({content: null, event: 'csv_upload', type: null})
  handleCSVUpload = (emails) => {
    this.setState(({value}) => {
      const updatedValue = value
        .trim()
        .split(delimiter)
        .filter((item) => item) // Remove any empty strings among items
        .concat(emails)
        .join(';\n');
      return {value: updatedValue};
    }, this.handleSubmit);
  };

  handleSend = () => {
    this.setState({
      requestInFlight: true
    });

    const promises = this.state.emails
      .toList()
      .filterNot(this.doesInvitationAlreadyExist)
      .map((email) => {
        /**
         * The following represents a polyfill of sorts for Promise.allSettled (incomplete browser support).
         * By default, Promise.all will break after encountering the first rejected promise among its arguments.
         * Below, we catch such rejections in advance and replace the error with a neutral object. We then parse
         * the responses below. See https://medium.com/trabe/using-promise-allsettled-now-e1767d43e480
         */
        const promise = ClassroomInvitationModelV1.getDefaultModel()
          .setEmail(email)
          .setSentEmailAt(null)
          .addRelationship({
            relation: masqueradeStore.getUserIdByMasqueradeState(),
            type: 'issuer_v1'
          })
          .addRelationship({
            relation: this.props.classroomId,
            type: 'classroom_v1'
          })
          .save();

        return promise
          .then((response) => ({
            email,
            response,
            status: 'fulfilled'
          }))
          .catch((reason) => ({
            email,
            reason,
            status: 'rejected'
          }));
      });

    /**
     * Chaining here for readability
     */
    Promise.all(promises)
      .then((invitations) => {
        // Parse our response payloads into a consumable format for our component state
        const parsedResponse = invitations.reduce(
          (acc, curr) => {
            if (curr.status === 'fulfilled') {
              acc.fulfilled.push(curr.email);
              return acc;
            }
            if (
              curr.reason?.response?.body?.errors?.find(
                (error) => error.code === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT
              )
            ) {
              acc.hardCapRejected.push(curr.email);
              return acc;
            }

            acc.rejected.push(curr.email);
            return acc;
          },
          {
            fulfilled: [],
            rejected: [],
            hardCapRejected: []
          }
        );

        return Promise.resolve(parsedResponse);
      })
      .then((parsedResponse) => {
        // Set the component state
        this.setState(({emails}) => ({
          emails: emails.subtract(parsedResponse.fulfilled),
          fulfilledInvitations: parsedResponse.fulfilled,
          mustConfirmSubmission: false,
          rejectedInvitations: parsedResponse.rejected,
          hardCapRejectedInvitations: parsedResponse.hardCapRejected,
          requestInFlight: false
        }));

        return Promise.resolve(parsedResponse.fulfilled);
      })
      .then((successfullySavedInvites) => {
        // Determine whether it's appropriate to fire the this.props.onSend callback
        if (successfullySavedInvites.length > 0) {
          return Promise.resolve();
        }
        return Promise.reject();
      })
      .then(this.props.onSend)
      .catch(() => {
        // If none of the invitations saved, then something must've gone wrong.
        addToast({
          message: 'There was a problem updating your roster. If the problem persists, contact us.',
          title: 'Something went wrong',
          type: 'danger'
        });
      });
  };

  doesInvitationAlreadyExist = (email) => {
    return (
      this.props.students.some((student) => student.getEmail() === email) ||
      this.props.invitations.some((invitation) => invitation.getEmail() === email)
    );
  };

  removeEmail = (email) => {
    this.setState(({emails}) => ({emails: emails.delete(email)}));
  };

  render() {
    const {
      classroomId,
      emailDomains,
      seatsRemaining,
      handleClose,
      restrictClassroomEnrollment,
      hardCap,
      ...rest
    } = this.props;

    const {fulfilledInvitations, rejectedInvitations, requestInFlight, hardCapRejectedInvitations} =
      this.state;

    const showSummary =
      fulfilledInvitations.length !== 0 ||
      rejectedInvitations.length !== 0 ||
      hardCapRejectedInvitations.length !== 0;

    const {isReady: hasFetchedLicenses, value: hasLicense} = doesUserBelongToAnyLicensedClassrooms(
      true // Query teacher's licenses
    );

    if (!hasFetchedLicenses || requestInFlight) {
      return (
        <Dialogue hideCloseButton {...rest}>
          <Dialogue.Body>
            <LoadingSpinnerLong />
          </Dialogue.Body>
        </Dialogue>
      );
    }

    if (!hasLicense) {
      return <IBEUnlicensedError handleClose={handleClose} {...rest} />;
    }
    if (showSummary) {
      return (
        <IBESummary
          onBackBtnClick={() =>
            this.setState({
              fulfilledInvitations: [],
              mustConfirmSubmission: false,
              rejectedInvitations: [],
              hardCapRejectedInvitations: []
            })
          }
          fulfilledInvitations={fulfilledInvitations}
          hardCapRejectedInvitations={hardCapRejectedInvitations}
          handleClose={handleClose}
          rejectedInvitations={rejectedInvitations}
          {...rest}
        />
      );
    }

    if (this.state.mustConfirmSubmission) {
      const validEmails = this.state.emails.toList().filterNot(this.doesInvitationAlreadyExist);
      const invalidEmails = this.state.emails.toList().filter(this.doesInvitationAlreadyExist);

      return (
        <IBEConfirmEmails
          classroomId={classroomId}
          handleClose={handleClose}
          validEmails={validEmails}
          invalidEmails={invalidEmails}
          onBackBtnClick={() => this.setState({mustConfirmSubmission: false})}
          onConfirm={this.handleSend}
          {...rest}
        />
      );
    }

    const showBanner = restrictClassroomEnrollment && !emailDomains.isEmpty();

    return (
      <Dialogue handleClose={handleClose} {...rest} paddingLevel={0} size='m'>
        <Dialogue.Title size='s' className='u-pad-lr_2 u-pad-t_1'>
          Invite Students by Email
        </Dialogue.Title>
        {hardCap && <HardCapWarningBanner seatsRemaining={seatsRemaining} />}
        <Dialogue.Body className='u-pad_3'>
          <TrackedTextAreaField
            border
            value={this.state.value}
            className='u-mar-b_2 u-max-width_text'
            error={Boolean(this.state.error)}
            label='Enter email address(es)'
            message={this.state.error}
            onChange={this.handleChange}
            onKeyPress={this.handleKeyPress}
            placeholder='Separate multiple email addressess using a comma, space, or semicolon'
            type='text'
          />
          <div className='u-display_flex u-align-items_center'>
            <TrackedButton className='u-mar-l_auto u-mar-b_2' onClick={this.handleSubmit}>
              Add
            </TrackedButton>
          </div>
          <EmailChips
            className='u-mar-b_2 u-mar-t_2 u-overflow-y_auto ibe-email-list'
            emails={this.state.emails}
            handleClick={this.removeEmail}
          />
          <UploadCSVBtn className='u-mar-r_auto u-pad-t_2' onUpload={this.handleCSVUpload} />
          {showBanner && (
            <Banner className='u-mar-t_2'>
              <span>
                Invitations are restricted to accounts ending in{' '}
                <ArrayToReadableFragment
                  arr={[...emailDomains.map((domain) => domain.getName())]}
                  as='strong'
                />
                .
              </span>
            </Banner>
          )}
        </Dialogue.Body>
        <div className='u-align-items_center u-mar-t_3 u-mar-b_2 u-display_flex u-pad_2'>
          <TrackedButton className='u-mar-l_auto' color='secondary' onClick={handleClose}>
            Cancel
          </TrackedButton>
          <TrackedButton
            className='u-mar-l_1'
            disabled={this.state.emails.isEmpty()}
            onClick={() => this.setState({mustConfirmSubmission: true})}
          >
            Send Invitations
          </TrackedButton>
        </div>
      </Dialogue>
    );
  }
}

export default InviteByEmail;
